<script setup lang="ts">
import FormDateInput from '@/core/forms/FormDateInput.vue';
import { EPosition } from 'common/enum/misc.enum';
import { toIsoDateTimeString } from 'common/utils/date.utils';
import { computed, nextTick, ref, useId, useTemplateRef } from 'vue';

const emit = defineEmits<{
  'update:date': [date: string];
  hide: [];
}>();

const props = withDefaults(
  defineProps<{
    anchorElement: HTMLElement | null;
    date: string;
    datePickerPosition?: EPosition;
    datePickerTeleport?: boolean | string | HTMLElement;
  }>(),
  {
    datePickerPosition: EPosition.BOTTOM_LEFT,
    datePickerTeleport: true,
  },
);

const dateInputRef = useTemplateRef('dateInputRef');
const dateInputName = `date-selection-${useId()}`;
const datePickerOpen = ref(false);
const datePickerMounted = ref(false);

const mountDatePicker = async (): Promise<void> => {
  if (datePickerMounted.value) return;
  datePickerMounted.value = true;
  await nextTick();
};

const handlePickedDate = (date: string): void => {
  emit('update:date', toIsoDateTimeString(date));
};

const show = async (): Promise<void> => {
  await mountDatePicker();
  await nextTick();
  dateInputRef.value?.open();
};
const hide = (): void => {
  dateInputRef.value?.close();
};

const handleDatePickerOpen = (): void => {
  datePickerOpen.value = true;
};

const handleDatePickerClose = (): void => {
  datePickerOpen.value = false;
  emit('hide');
};

defineExpose({
  show,
  hide,
  isOpen: computed(() => datePickerOpen.value),
});
</script>

<template>
  <span class="hidden">
    <FormDateInput
      v-if="datePickerMounted"
      ref="dateInputRef"
      :name="dateInputName"
      theme="compact"
      :value="props.date"
      :anchor-element="props.anchorElement"
      :position="props.datePickerPosition"
      :teleport="props.datePickerTeleport"
      @close="handleDatePickerClose"
      @open="handleDatePickerOpen"
      @update:value="handlePickedDate"
    />
  </span>
</template>
